<script setup lang="ts">
import DocumentAccordion from "@/components/DocumentAccordion.vue"
import Pdf from "@/components/Pdf.vue"
import type { Invoice } from "@/model/order/invoice"
import { InvoiceType } from "@/model/order/invoice-type"
import type { PayOrderDataDto } from "@/model/order/pay-order-data-dto"
import type { ShopOrderConfirmationTask } from "@/model/order/shop-order-confirmation-task"
import ShopOrderTable from "@/pages/Tasks/Tasks/internal/ShopOrderTable.vue"
import type { TasksProps } from "@/pages/Tasks/Tasks/tasks-props"
import { defineTranslations } from "@/utils/defineTranslations"
import { useApp, useHttpGet } from "@/vf"
import { computed, type Ref, ref, toRefs } from "vue"

const props = defineProps<TasksProps<ShopOrderConfirmationTask>>()
const { task } = toRefs(props)
const { data } = (await useHttpGet<PayOrderDataDto>(
    `/order/shop/${task.value.shopOrder?.shopConfiguration?.id}/${task.value.shopOrder?.id}/pay-order`,
)) as { data: Ref<PayOrderDataDto> }

const { apiLink } = useApp()
const openDocument = ref<string>(data.value.invoices?.length ? "" : "orderConfirmation")
const invoices = computed(() => data.value.invoices ?? [])

function toggleDocument(document: string) {
    openDocument.value = openDocument.value === document ? "" : document
}

function invoiceLink(invoice: Invoice) {
    return `/order/shop/${task.value.shopOrder?.shopConfiguration?.id}/${task.value.shopOrder?.id}/invoice/${invoice.id}`
}

function invoiceTitle(invoice: Invoice) {
    const label = invoice.type === InvoiceType.CreditNote ? trans("credit_note") : trans("invoice")
    return invoice.invoiceNumber ? `${label} ${invoice.invoiceNumber}` : label
}

const trans = defineTranslations({
    "order_confirmation": {
        de: "Bestellbestätigung",
        en: "Order confirmation",
    },
    "order_confirmation.download": {
        de: "Bestellbestätigung herunterladen",
        en: "Download order confirmation",
    },
    "invoice": {
        de: "Rechnung",
        en: "Invoice",
    },
    "invoice.download": {
        de: "Rechnung herunterladen",
        en: "Download invoice",
    },
    "credit_note": {
        de: "Gutschrift",
        en: "Credit note",
    },
    "credit_note.download": {
        de: "Gutschrift herunterladen",
        en: "Download credit note",
    },
})

const orderConfirmationLink = `/order/shop/${props.task.shopOrder?.shopConfiguration?.id}/${props.task.shopOrder?.id}/order-confirmation`
</script>
<template>
    <div class="p-5">
        <b>{{ $t("@tasks:tasks.shop.order_confirmation.description.title") }}</b>
        <div>{{ $t("@tasks:tasks.shop.order_confirmation.description.text") }}</div>

        <div class="row mt-4">
            <div class="col-xl-6 col-lg-6">
                <DocumentAccordion
                    :title="trans('order_confirmation')"
                    :open="openDocument === 'orderConfirmation'"
                    @toggle="toggleDocument('orderConfirmation')"
                >
                    <div class="text-center mb-3">
                        <a :href="apiLink(orderConfirmationLink + '?download=1')" class="btn btn-primary">
                            <i class="fa fa-fw fa-download"></i>
                            {{ trans("order_confirmation.download") }}
                        </a>
                    </div>
                    <Pdf :file="orderConfirmationLink" />
                </DocumentAccordion>

                <DocumentAccordion
                    v-for="invoice in invoices"
                    :key="invoice.id"
                    class="mt-3"
                    :title="invoiceTitle(invoice)"
                    :open="openDocument === invoice.id"
                    @toggle="toggleDocument(invoice.id!)"
                >
                    <div class="text-center mb-3">
                        <a :href="apiLink(invoiceLink(invoice) + '?download=1')" class="btn btn-primary">
                            <i class="fa fa-fw fa-download"></i>
                            {{
                                invoice.type === InvoiceType.CreditNote
                                    ? trans("credit_note.download")
                                    : trans("invoice.download")
                            }}
                        </a>
                    </div>
                    <Pdf :file="invoiceLink(invoice)" />
                </DocumentAccordion>
            </div>
            <div class="col-12 col-lg-6 order-first order-lg-last">
                <ShopOrderTable
                    :shop-order-id="task.shopOrder?.id!"
                    :shop-configuration-id="task.shopOrder?.shopConfiguration?.id!"
                    assignments-editable
                />
            </div>
        </div>
    </div>
</template>
